/**
 * Staff360Page — Read-only holistic dashboard for a single staff member.
 *
 * Route: /staff-360/:userId  (HR / Admin / IT Admin / Super Admin / Self)
 *
 * The user explicitly asked for this to be STRICTLY READ-ONLY. Every clickable element
 * routes the viewer to the underlying granular page (e.g., Attendance graph → /attendance,
 * Salary card → /payroll, Leave card → /leave). No edit buttons live on this page.
 */
import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  ArrowLeft, User as UserIcon, BadgeCheck, Calendar, Cake, Heart, ShieldCheck,
  Activity, CalendarClock, IndianRupee, LineChart as LineChartIcon, Briefcase,
  Phone, MapPin, Droplet, ChevronRight, Loader2, AlertCircle, ClipboardList,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  AreaChart, Area, Cell,
} from 'recharts';

const fmtINR = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const Staff360Page = () => {
  const { userId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const isSelf = user?.id === userId;
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/staff-360/${userId}`);
        if (!cancelled) setData(r.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load staff 360');
        if (err.response?.status === 403 || err.response?.status === 404) navigate(-1);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, navigate]);

  const attData = useMemo(() => (data?.attendance?.daily_series || []).map(d => ({
    day: d.date.slice(5),  // MM-DD
    hours: d.hours,
    status: d.status,
  })), [data]);
  const revData = useMemo(() => (data?.performance?.daily_revenue || []).map(d => ({
    day: d.date.slice(5),
    revenue: d.revenue,
  })), [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400 gap-2">
        <Loader2 className="animate-spin" size={20} /> Loading Staff 360°…
      </div>
    );
  }
  if (!data) return null;

  const photoUrl = data.profile.profile_photo_url ? `${backendUrl}${data.profile.profile_photo_url}` : null;
  const fullName = `${data.user.first_name || ''} ${data.user.last_name || ''}`.trim();

  const goToAttendance = () => navigate(isSelf ? '/my-attendance' : '/attendance');
  const goToLeave = () => navigate('/leave');
  const goToPayroll = () => navigate('/payroll');
  const goToProfile = () => navigate(isSelf ? '/my-profile' : `/staff?focus=${userId}`);
  const goToFinanceForDoctor = () => navigate(`/finance-dashboard?staff_id=${userId}`);

  // Build a small previous-page link if we came from a known list page
  const backLabel = location.state?.from ? 'Back' : 'Back';

  return (
    <div className="space-y-5" data-testid="staff-360-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <button onClick={() => navigate(-1)} className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1" data-testid="staff-360-back">
          <ArrowLeft size={12} /> {backLabel}
        </button>
        <div className="ml-auto text-xs text-slate-400">Read-only · {data.window.days}-day window · click any card to drill down</div>
      </div>

      {/* Identity Card */}
      <Card data-testid="staff-360-identity">
        <CardContent className="p-5 flex flex-col md:flex-row gap-5 items-start md:items-center">
          <div className="h-24 w-24 rounded-2xl bg-slate-100 overflow-hidden flex items-center justify-center border border-slate-200 flex-shrink-0">
            {photoUrl ? (
              <img src={photoUrl} alt={fullName} className="w-full h-full object-cover" data-testid="staff-360-photo" />
            ) : (
              <UserIcon size={40} className="text-slate-400" />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-slate-900">{fullName}</h1>
              <span className="text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{data.user.role_name || data.user.role_code || 'Staff'}</span>
              {!data.user.is_active && <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">Inactive</span>}
            </div>
            <p className="text-sm text-slate-500 mt-0.5">{data.profile.designation || '—'} · {data.profile.department || '—'}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-[11px]">
              <Stat icon={BadgeCheck} label="Employee ID" value={data.profile.employee_code || data.user.user_id || '—'} mono />
              <Stat icon={Calendar} label="Joined" value={fmtDate(data.profile.joining_date)} />
              <Stat icon={Cake} label="DOB" value={fmtDate(data.profile.date_of_birth)} />
              <Stat icon={Heart} label="Anniversary" value={fmtDate(data.profile.wedding_anniversary)} />
              <Stat icon={Phone} label="Phone" value={data.user.phone || '—'} />
              <Stat icon={MapPin} label="Address" value={data.profile.address || '—'} small />
              <Stat icon={Droplet} label="Blood Group" value={data.profile.blood_group || '—'} />
              <Stat icon={Briefcase} label="Employment" value={data.profile.employment_type || '—'} />
            </div>
          </div>
          <div className="flex flex-col gap-2 self-start">
            <Button variant="outline" size="sm" onClick={goToProfile} data-testid="staff-360-view-profile">
              <ClipboardList size={14} className="mr-1.5" /> View Profile
              <ChevronRight size={12} className="ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* KYC + Attendance + Leave + Salary cards row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <DrilldownCard
          icon={ShieldCheck}
          tone={data.kyc.is_complete ? 'emerald' : 'amber'}
          title="KYC Completion"
          value={`${data.kyc.completion_pct}%`}
          sub={data.kyc.is_complete ? 'All set!' : `${(data.kyc.missing_required?.length || 0) + (data.kyc.missing_encouraged?.length || 0)} missing`}
          onClick={goToProfile}
          testid="staff-360-kyc-card"
        />
        <DrilldownCard
          icon={Activity}
          tone={data.attendance.rate_pct >= 90 ? 'emerald' : data.attendance.rate_pct >= 75 ? 'amber' : 'rose'}
          title="Attendance Rate"
          value={`${data.attendance.rate_pct}%`}
          sub={`${data.attendance.present_days} present · ${data.attendance.absent_days} absent · ${data.attendance.total_hours}h`}
          onClick={goToAttendance}
          testid="staff-360-attendance-card"
        />
        <DrilldownCard
          icon={CalendarClock}
          tone="indigo"
          title="Leave (this year)"
          value={`${data.leave.approved_days_this_year} days`}
          sub={`${data.leave.open_count} pending · ${data.leave.balances.length} balances`}
          onClick={goToLeave}
          testid="staff-360-leave-card"
        />
        {data.salary !== null && data.salary !== undefined ? (
          <DrilldownCard
            icon={IndianRupee}
            tone="violet"
            title="Salary"
            value={data.salary.monthly_salary !== null && data.salary.monthly_salary !== undefined ? fmtINR(data.salary.monthly_salary) : 'Hidden'}
            sub={data.salary.last_payslip ? `Last payslip · ${data.salary.last_payslip.month}` : 'Monthly'}
            onClick={goToPayroll}
            testid="staff-360-salary-card"
          />
        ) : (
          <DrilldownCard
            icon={IndianRupee}
            tone="slate"
            title="Salary"
            value="Restricted"
            sub="You don't have permission"
            testid="staff-360-salary-restricted"
          />
        )}
      </div>

      {/* Attendance bar chart */}
      <Card data-testid="staff-360-attendance-chart">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><Activity size={14} /> Attendance · last {data.window.days} days</CardTitle>
          <Button size="sm" variant="ghost" className="text-xs" onClick={goToAttendance} data-testid="staff-360-attendance-link">
            Open Attendance <ChevronRight size={12} className="ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {attData.length === 0 ? (
            <p className="text-sm text-slate-400 py-8 text-center">No attendance data in this window.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={attData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }} onClick={goToAttendance} style={{ cursor: 'pointer' }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={Math.max(0, Math.floor(attData.length / 10))} />
                <YAxis tick={{ fontSize: 10 }} domain={[0, 12]} />
                <Tooltip formatter={(v, n, e) => [`${v}h`, e.payload.status]} />
                <Bar dataKey="hours" radius={[4, 4, 0, 0]}>
                  {attData.map((d, i) => (
                    <Cell key={i} fill={d.status === 'present' ? '#10b981' : d.status === 'half_day' ? '#f59e0b' : d.status === 'on_leave' ? '#6366f1' : '#e2e8f0'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Doctor revenue trend */}
      {data.performance.is_doctor && (
        <Card data-testid="staff-360-revenue-chart">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <LineChartIcon size={14} /> Revenue Generated · last {data.window.days} days
              <span className="text-xs font-normal text-slate-500 ml-2">{fmtINR(data.performance.revenue_30d)} · {data.performance.patients_30d} patients · {data.performance.appointments_30d} appts</span>
            </CardTitle>
            <Button size="sm" variant="ghost" className="text-xs" onClick={goToFinanceForDoctor} data-testid="staff-360-revenue-link">
              Open Finance Dashboard <ChevronRight size={12} className="ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {revData.length === 0 ? (
              <p className="text-sm text-slate-400 py-8 text-center">No payment data attributed to this doctor.</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={revData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }} onClick={goToFinanceForDoctor} style={{ cursor: 'pointer' }}>
                  <defs>
                    <linearGradient id="staffRevGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.5} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={Math.max(0, Math.floor(revData.length / 10))} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                  <Tooltip formatter={(v) => fmtINR(v)} />
                  <Area type="monotone" dataKey="revenue" stroke="#8b5cf6" strokeWidth={2} fill="url(#staffRevGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* KYC missing list */}
      {!data.kyc.is_complete && (
        <Card data-testid="staff-360-kyc-missing">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-amber-700"><AlertCircle size={14} /> KYC pending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5 text-xs">
              {data.kyc.missing_required.map(m => (
                <div key={m.key} className="flex items-center justify-between border-b border-slate-100 py-1.5">
                  <span className="text-slate-700">{m.label}</span>
                  <span className="text-rose-600 font-semibold uppercase text-[10px]">Required</span>
                </div>
              ))}
              {data.kyc.missing_encouraged.map(m => (
                <div key={m.key} className="flex items-center justify-between border-b border-slate-100 py-1.5">
                  <span className="text-slate-700">{m.label}</span>
                  <span className="text-amber-600 font-semibold uppercase text-[10px]">Encouraged</span>
                </div>
              ))}
            </div>
            <Button size="sm" variant="outline" className="mt-3 w-full" onClick={goToProfile} data-testid="staff-360-kyc-fix">
              Update Profile <ChevronRight size={12} className="ml-1" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Recent leaves */}
      {(data.leave.recent || []).length > 0 && (
        <Card data-testid="staff-360-leave-table">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2"><CalendarClock size={14} /> Recent Leaves</CardTitle>
            <Button size="sm" variant="ghost" className="text-xs" onClick={goToLeave}>Open Leave <ChevronRight size={12} className="ml-1" /></Button>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left">From</th>
                    <th className="px-3 py-2 text-left">To</th>
                    <th className="px-3 py-2 text-right">Days</th>
                    <th className="px-3 py-2 text-left">Reason</th>
                    <th className="px-3 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.leave.recent.map(l => (
                    <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={goToLeave}>
                      <td className="px-3 py-1.5">{fmtDate(l.from_date)}</td>
                      <td className="px-3 py-1.5">{fmtDate(l.to_date)}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{l.days}</td>
                      <td className="px-3 py-1.5 truncate max-w-[200px]">{l.reason || '—'}</td>
                      <td className="px-3 py-1.5">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                          l.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                          l.status === 'rejected' ? 'bg-rose-100 text-rose-700' :
                          l.status === 'cancelled' ? 'bg-slate-100 text-slate-500' :
                          'bg-amber-100 text-amber-700'
                        }`}>{l.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, mono, small }) => (
  <div className="bg-slate-50 rounded-md px-2.5 py-1.5">
    <div className="flex items-center gap-1 text-[9px] uppercase font-bold text-slate-400 tracking-wider"><Icon size={10} /> {label}</div>
    <div className={`text-slate-700 ${mono ? 'font-mono' : 'font-medium'} ${small ? 'text-[10px] truncate' : 'text-xs'}`}>{value}</div>
  </div>
);

const DrilldownCard = ({ icon: Icon, tone, title, value, sub, onClick, testid }) => {
  const toneMap = {
    emerald: 'bg-emerald-100 text-emerald-700',
    amber: 'bg-amber-100 text-amber-700',
    rose: 'bg-rose-100 text-rose-700',
    indigo: 'bg-indigo-100 text-indigo-700',
    violet: 'bg-violet-100 text-violet-700',
    slate: 'bg-slate-100 text-slate-500',
  };
  return (
    <Card className={onClick ? 'cursor-pointer hover:shadow-md hover:border-emerald-300 transition' : ''} onClick={onClick} data-testid={testid}>
      <CardContent className="p-4">
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${toneMap[tone]}`}><Icon size={16} /></div>
        <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mt-2">{title}</p>
        <p className="text-xl font-bold text-slate-900 mt-0.5">{value}</p>
        {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
        {onClick && <p className="text-[9px] text-emerald-700 mt-2 inline-flex items-center">Open <ChevronRight size={10} className="ml-0.5" /></p>}
      </CardContent>
    </Card>
  );
};

export default Staff360Page;
